<template>
  <div class="card-sales">
    <page-header :title="showPurgatorySales ? 'Purgatory Sales' : 'All Sales'" small>
      <template #actions>
        <div class="actions">
          <icon-button
            icon="view_list"
            :class="{ success: salesViewType === 'list' }"
            tooltip="Full details view"
            @click="salesViewType = 'list'"
          />

          <icon-button
            icon="list"
            :class="{ success: salesViewType === 'table' }"
            tooltip="Table view"
            @click="salesViewType = 'table'"
          />

          <template v-if="isLadderCard">
            <icon-button
              v-if="isStaffView"
              tooltip="Initial scrape"
              icon="move_to_inbox"
              :loading="scraping"
              @click="startInitialScrape(false)"
            />

            <icon-button
              icon="inventory"
              :tooltip="showPurgatorySales ? 'Hide Purgatory Sales' : 'Show Purgatory Sales'"
              @click="showPurgatorySales = !showPurgatorySales"
            />
          </template>

          <icon-button icon="close" @click="close" />
        </div>
      </template>
    </page-header>

    <div v-show="!showPurgatorySales" class="filters">
      <div class="sorting">
        <select-input
          id="sort"
          v-model="sort"
          label="Sort"
          :options="sortOptions"
        />
      </div>

      <div v-if="!isPrivateCollectionCard" class="sorting">
        <date-input
          id="maxdate"
          v-model="dateFilter"
          label="Max Date"
          has-clear
          :min="minDateFilter"
        />
      </div>
    </div>

    <empty-state
      v-if="showEmptyState && (showPurgatorySales || (!isPrivateCollectionCard && !isStaffView))"
      icon="point_of_sale"
      :title="showPurgatorySales ? 'There are no sales for this card in purgatory.' : 'There are no sales for this card yet'"
    />

    <empty-state
      v-else-if="showEmptyState && isStaffView"
      icon="not_started"
      title="Add sales for this card to estimate the value automatically"
      :description="isStaffView ? 'After you have the image selected run this to get the pop and sales scraped' : ''"
    >
      <btn
        loading-text="Scraping"
        :loading="scraping"
        @click="handleAdminInit"
      >
        {{ adminStartButtonText }}
      </btn>
    </empty-state>

    <template v-else-if="loaded">
      <div v-if="salesViewType === 'list'" class="list">
        <card-sales-row
          v-for="sale in computedSales"
          :key="sale.id"
          :sale="sale"
          :purgatory="showPurgatorySales"
          :is-ladder-card="isLadderCard"
          @deleteSale="deleteSale"
          @moveToSales="moveToSales"
          @moveToPurgatory="moveToPurgatory"
          @addToHeadlines="addToHeadlines"
          @viewSalesHistory="viewSalesHistory"
          @viewTerapeak="viewTerapeak"
        />
      </div>

      <table v-else class="table-list">
        <thead>
          <th>Date</th>
          <th right>
            Price
          </th>
          <th right>
            Type
          </th>
          <th right>
            Platform
          </th>
          <th class="action" />
        </thead>

        <tbody>
          <card-sales-table-row
            v-for="sale in computedSales"
            :key="sale.id"
            :sale="sale"
            :purgatory="showPurgatorySales"
            :is-ladder-card="isLadderCard"
            @deleteSale="deleteSale"
          />
        </tbody>
      </table>

      <infinite-scroll
        scroll-parent-id="side-panel"
        :loading="loading"
        :has-more-data="hasMoreData"
        @load="loadSales(false)"
      />
    </template>

    <spinner v-else />

    <scroll-top is-side-panel />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import {
  cardService,
  authService,
  saleService,
  searchService,
} from '@/services';

import { SELLING_PLATFORM_OPTIONS, LISTING_TYPE_OPTIONS } from '@cardladder/shared/constants';
import { getTerapeakLink } from '@cardladder/shared/utils/format.util';

import CardSalesRow from './CardSalesRow';
import CardSalesTableRow from './CardSalesTableRow';

export default {
  components: {
    CardSalesRow,
    CardSalesTableRow,
  },

  props: {
    id: String,
  },

  data() {
    return {
      cardId: null,
      card: null,
      limit: 30,
      sales: [],
      loaded: false,
      loading: false,
      scraping: false,
      showPurgatorySales: false,
      queryCursor: null,
      hasMoreData: true,
      sellingPlatformOptions: SELLING_PLATFORM_OPTIONS,
      listingTypeOptions: LISTING_TYPE_OPTIONS,
      dateFilter: null,
      minDateFilter: moment().subtract(20, 'years').toDate(),
      sort: 'date:desc',
      sortOptions: [
        { value: 'date:desc', label: 'Recently Sold' },
        { value: 'date:asc', label: 'Date Sold Oldest' },
        { value: 'price:desc', label: 'Highest Price' },
        { value: 'price:asc', label: 'Lowest Price' },
      ],
    };
  },

  created() {
    this.load();
  },

  mounted() {
    this.checkForSalesDate();
    this.checkForSalesInit();
  },

  watch: {
    sort(value) {
      if (!this.dateFilter) {
        this.refresh();
      } else if (value !== 'date:desc') {
        this.dateFilter = null;
      } else {
        this.refresh();
      }
    },

    dateFilter(value) {
      if (this.sort === 'date:desc') {
        this.refresh();
      } else if (value) {
        this.sort = 'date:desc';
      } else {
        this.refresh();
      }
    },

    showPurgatorySales() {
      this.refresh();
    },

    id(to, from) {
      if (to !== from) {
        this.loaded = false;
        this.sales = [];
        this.hasMoreData = true;
        this.queryCursor = null;
        this.load(true);
      }

      this.checkForSalesInit();
    },

    '$route.query.date': {
      handler() {
        this.checkForSalesDate();
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    salesViewType: {
      get() {
        return this.$store.state.salesViewType;
      },

      set(value) {
        this.$store.commit('SET_SALES_VIEW_TYPE', value);
      },
    },

    isLadderCard() {
      return Boolean(this.cardId);
    },

    isStaffView() {
      return Boolean(this.isStaff && this.parentRoute === 'ladder');
    },

    isPrivateCollectionCard() {
      return this.parentRoute === 'collection' && !this.isLadderCard;
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.dateFilter === null && this.sales.length === 0;
    },

    parentRoute() {
      return this.$route.query.profile || 'ladder';
    },

    adminNeedsEdit() {
      return !this.card || !this.card.ebayQuery;
    },

    adminStartButtonText() {
      return this.adminNeedsEdit ? 'Fill Out Query' : 'Initialize Card';
    },

    hidden() {
      return this.card && this.card.hidden;
    },

    computedSales() {
      if (!this.sales || this.sales.length === 0) {
        return [];
      }

      const cardLabel = this.card?.label || '';

      return this.sales.map((hit, i) => {
        const sale = {
          ...hit,
          alertPrice: false,
          title: hit.title || cardLabel,
          displayDate: moment(hit.date).format('MMM Do, YYYY'),
        };

        const listingTypeObject = LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType);
        const platformObject = SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform);

        sale.listingType = listingTypeObject ? listingTypeObject.label : hit.listingType || '';
        sale.platform = platformObject ? platformObject.label : hit.platform || '';

        if (this.hidden && this.sales[i + 1] !== undefined && typeof this.sales[i + 1].price === 'number' && typeof sale.price === 'number') {
          if ((Math.abs(sale.price - this.sales[i + 1].price) / this.sales[i + 1].price) >= 1) {
            sale.alertPrice = true;
          }
        }

        return sale;
      });
    },
  },

  methods: {
    async load(reset = false) {
      this.loaded = false;

      try {
        if (this.parentRoute === 'collection') {
          const collectionCard = await authService.getCollectionCard(this.user.uid, this.collection.id, this.id);
          this.cardId = collectionCard.cardId;
          this.card = collectionCard;
        } else if (this.parentRoute === 'watchlist') {
          const watchlistCard = await authService.getWatchlistCard(this.user.uid, this.id);
          this.cardId = watchlistCard.cardId;
        } else {
          this.cardId = this.id;
          this.card = await cardService.getCard(this.cardId);
        }

        await this.loadSales(reset);
      } catch (e) {
        this.$toasted.error('Error loading the card');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    async loadSales(reset = false) {
      this.loading = true;

      try {
        const sortField = this.sort.split(':')[0];
        const sortDirection = this.sort.split(':')[1];
        const dateFilter = this.dateFilter ? moment(this.dateFilter).add(1, 'days').toDate() : null;
        const shouldPullSalesFromProfileInfo = this.card && this.card.useGradedCardAutomatic && this.card.profileId && this.card.grade && this.card.grader;

        let salesData = null;

        if (this.isPrivateCollectionCard && (this.card?.ebayQuery || shouldPullSalesFromProfileInfo)) {
          const input = {
            index: 'salesarchive',
            page: 0,
            limit: 100,
            sort: sortField,
            direction: sortDirection,
          };

          if (shouldPullSalesFromProfileInfo) {
            input.filters = {
              profileId: this.card.profileId,
              grade: this.card.grade,
              grader: this.card.grader,
              qualifierType: this.card.qualifierType,
              autographGrade: this.card.autographGrade,
            };
          } else if (this.card.ebayQuery) {
            input.query = this.card.ebayQuery;
          }

          const data = await searchService.search(input);

          salesData = data.hits;
          this.hasMoreData = false;
        } else {
          let response;

          if (this.showPurgatorySales) {
            response = await saleService.getPurgatorySalesForCard(
              this.cardId,
              this.limit,
            );
          } else {
            response = await saleService.getSales(
              this.cardId,
              this.limit,
              this.queryCursor,
              sortField,
              sortDirection,
              dateFilter,
            );
          }

          const { data, cursor } = response;

          this.queryCursor = cursor;
          salesData = data;
        }

        if (reset) {
          this.sales = salesData;
        } else {
          this.sales = [...this.sales, ...salesData];
        }

        if (this.showPurgatorySales || salesData.length < this.limit) {
          this.hasMoreData = false;
        }

        this.loaded = true;
      } catch (e) {
        this.$toasted.error('Error loading the sales');
        Sentry.captureException(e);
      }

      this.loading = false;
    },

    async deleteSale(id) {
      if (window.confirm('Are you sure you want to delete this sale?')) {
        try {
          if (this.showPurgatorySales) {
            await saleService.deletePurgatorySale(id);
          } else {
            await saleService.deleteSale(this.cardId, id);
          }

          this.$toasted.success('Deleted the sale!');
          this.sales = this.sales.filter((sale) => {
            return this.showPurgatorySales ? sale.itemId !== id : sale.id !== id;
          });
        } catch (e) {
          this.$toasted.error('Error deleting the sale');
          Sentry.captureException(e);
        }
      }
    },

    async moveToSales(sale) {
      if (window.confirm('Are you sure you want to move this to sales?')) {
        try {
          await saleService.confirmSale(sale, true);
          this.$toasted.success('Moved to sales');
          this.sales = this.sales.filter(({ itemId }) => itemId !== sale.itemId);
        } catch (e) {
          this.$toasted.error('Error moving to sales');
        }
      }
    },

    async moveToPurgatory(sale) {
      if (window.confirm('Are you sure you want to move this to purgatory?')) {
        try {
          const ladderCard = await cardService.getCard(this.cardId);
          const purgatorySale = {
            ...sale,
            itemId: sale.itemId || Date.now().toString(),
            title: sale.title || ladderCard.label,
            cardId: this.cardId,
            cardImage: ladderCard.image,
            cardLabel: ladderCard.label,
            condition: ladderCard.condition,
            label: `${ladderCard.player} ${ladderCard.year} ${ladderCard.set} ${ladderCard.variation || ''} ${ladderCard.condition}`,
            marketValue: ladderCard.stats?.marketValue || null,
            guideValue: ladderCard.stats?.guideValue || null,
          };

          await saleService.moveSaleToPurgatory(this.cardId, purgatorySale);

          this.$toasted.success('Moved to purgatory');
          this.sales = this.sales.filter(({ id }) => id !== sale.id);
        } catch (e) {
          this.$toasted.error('Error moving to purgatory');
        }
      }
    },

    async addToHeadlines(sale) {
      if (window.confirm('Are you sure you want add this sale as a possible headline?')) {
        try {
          const ladderCard = await cardService.getCard(this.cardId);
          const possibleHeadline = {
            ...sale,
            itemId: sale.id,
            cardId: this.cardId,
            cardLabel: ladderCard.label,
            employee: ladderCard.employee,
          };

          await saleService.flagForHeadlines(possibleHeadline);
          this.$toasted.success('Sale is added to possible headlines');
        } catch (e) {
          this.$toasted.error('Error flagging the sale');
        }
      }
    },

    async viewSalesHistory(sale) {
      window.open(`/sales-history?saleId=${sale.itemId}&q=${encodeURIComponent(this.card.ebayQuery)}`, '_blank');
    },

    async viewTerapeak(sale) {
      window.open(getTerapeakLink(sale.title, true), '_blank');
    },

    handleAdminInit() {
      if (this.adminNeedsEdit) {
        this.$bus.$emit('admin-edit-card', this.cardId);
      } else {
        this.startInitialScrape();
      }
    },

    checkForSalesInit() {
      if (this.$route.query.init) {
        setTimeout(() => {
          this.startInitialScrape(true);
          let query = clone(this.$route.query);
          delete query.init;
          this.$router.replace({ query });
        }, 1000);
      }
    },

    checkForSalesDate() {
      if (this.$route.query.date) {
        setTimeout(() => {
          this.dateFilter = new Date(this.$route.query.date);
          let query = clone(this.$route.query);
          delete query.date;
          this.$router.replace({ query });
        }, 300);
      }
    },

    async startInitialScrape(force = false) {
      if (force || window.confirm('Are you sure you want to scrape all data for this card? Keep in mind this will delete ALL existing sales.')) {
        this.scraping = true;

        try {
          await firebase.functions().httpsCallable('httpInitialSalesScrape')({ card: this.cardId, uid: this.user.uid });

          this.$toasted.success('Card initial scrape done!');
        } catch (e) {
          this.$toasted.error('Failed to pull in sales');
          Sentry.captureException(e);
        }

        this.refresh();

        this.scraping = false;
      }
    },

    refresh() {
      this.loaded = false;
      this.sales = [];
      this.hasMoreData = true;
      this.queryCursor = null;
      this.loadSales(true);
    },

    close() {
      const query = { ...this.$route.query };
      delete query.showSales;
      delete query.date;
      this.$router.push({ query });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-sales {
  padding: $gp;
}

.list {
  margin: 0 (-$gp);
}

.actions {
  white-space: nowrap;

  > *:not(:last-child) {
    margin-right: $gp * .5;
  }
}

.filters {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.sorting {
  width: 300px;

  &:not(:last-child) {
    margin-right: $gp * .5;
  }
}

.sales-container {
  margin-top: $gp;
}

.results-header {
  margin-bottom: $gp;

  h5 {
    margin-bottom: 0;
  }
}

@media($medium-down) {
  .filters {
    flex-direction: column;
  }

  .sorting {
    width: 100%;

    &:not(:last-child) {
      margin-right: 0;
    }
  }
}
</style>
